import * as Analytics from 'analytics/analytics'
import { logOperational } from 'analytics/snowplow/events'
import request from 'api/requestUtils'
import config from 'constants/config'
import { detectOfferTypeFromId } from 'lib/offerUtils'
import debounce from 'lodash/debounce'
import qs from 'qs'
import { Recommendation, offerRecommendationMap } from './mappers/recommendationMap'

function getLereEnvParameters(): Record<string, string> {
  return config.LERE_USE_MOCKS ? { use_mocks: 'true' } : {}
}

export function getLereApiHostOverride(): string | undefined {
  return config.LERE_OVERRIDE_API_HOST || undefined
}

interface ApiError {
  status: number;
  errors: string;
}

function isApiError(obj: any): obj is ApiError {
  return Number.isFinite(obj.status) && obj.errors
}

function logError(apiTag: string, error?: any, extraParams: Record<string, any> = {}) {
  // __ is just a convenient separator - it is unlikely error messages will have double __ in them
  const label = (() => {
    if (error instanceof Error) {
      return `${error.name}__${error.message}`
    }
    if (isApiError(error)) {
      return `${error.status}__${error.errors}`
    }
    return error
  })()

  Analytics.track('log-operational', logOperational(apiTag, `${label}__${JSON.stringify(extraParams)}__errored`))
}

interface RecommendationURLParams {
  check_in?: string;
  check_out?: string;
  available_month?: string;
  duration?: number;
  region?: string;
  allow_multi_offers_same_property?: boolean
  flex?: number;
}

class RecommendationsURLBuilder {
  // @ts-ignore
  private baseUrl: string
  private offerId: string
  private queryParams: Record<string, string | number | boolean> = {}

  constructor(offerId: string) {
    this.offerId = offerId
  }

  withCalendarBaseUrl() {
    this.baseUrl = `/api/recommendation/offers/${this.offerId}/calendar`
    return this
  }

  withBaseUrl() {
    this.baseUrl = `/api/recommendation/offers/${this.offerId}`
    return this
  }

  withCacheBaseUrl() {
    this.baseUrl = `/api/recommendation/offers/${this.offerId}/cache`
    return this
  }

  withQueryParams(queryParams: RecommendationURLParams) {
    this.queryParams = { ...this.queryParams, ...queryParams }
    return this
  }

  withLereEnvParameters() {
    this.queryParams = { ...this.queryParams, ...getLereEnvParameters() }
    return this
  }

  withRoomParams(rooms?: App.Occupants[]) {
    if (rooms?.length) {
      const occupancy = rooms.map(room => {
        let roomParam = `${room.adults}`
        if (room?.childrenAge?.length) {
          roomParam += `-${room.childrenAge.join(',')}`
        }
        return roomParam
      }).join(';')
      this.queryParams.occupancy = occupancy
    }
    return this
  }

  build() {
    return `${this.baseUrl}?${qs.stringify(this.queryParams)}`
  }
}

export interface Filters {
  checkIn?: string;
  availableMonth?: string;
  rooms?: App.Occupants[];
  duration?: number;
}

interface LereResult {
  recommendations: App.Recommendation[];
  lereVersion: string | undefined;
}

export async function getRecommendations(offerId: string, region: string, checkIn?: string, checkOut?: string, flex?: number): Promise<LereResult> {
  try {
    const builder = new RecommendationsURLBuilder(offerId)
      .withLereEnvParameters()
      .withQueryParams({
        region,
        check_in: checkIn,
        check_out: checkOut,
        flex,
      })
    const url = builder.withBaseUrl().build()
    const response = await request.get<App.ApiResponse<Recommendation[]>>(
      url,
      { credentials: 'include' },
      getLereApiHostOverride(),
    )
    const { lere_version } = response.meta || {}

    const recommendations = response.result.map(recommendation => offerRecommendationMap(recommendation))
    return { recommendations, lereVersion: lere_version }
  } catch (error) {
    logError('getRecommendations', error, { offerId, region, checkIn, checkOut })
    throw error
  }
}

export function getChildrenAgesValid(rooms?: App.Occupants[]) {
  return (rooms || []).every((room) => {
    return (room.childrenAge || []).every((age) => age >= 0)
  })
}

export async function getCalendarRecommendations(
  offerId: string,
  region: string,
  filters: Filters,
): Promise<LereResult> {
  try {
    const { checkIn, rooms, availableMonth, duration } = filters

    if (!getChildrenAgesValid(rooms)) {
      throw new Error('Some children ages are < 0')
    }

    const useCalendarAPI = !checkIn
    const builder = new RecommendationsURLBuilder(offerId)
      .withLereEnvParameters()
      .withQueryParams({
        check_in: checkIn,
        duration,
        region,
        allow_multi_offers_same_property: true,
      })
      .withRoomParams(rooms)

    if (useCalendarAPI) {
      builder.withCalendarBaseUrl().withQueryParams({ available_month: availableMonth })
    } else {
      builder.withBaseUrl()
    }

    const url = builder.build()
    const response = await request.get<App.ApiResponse<Recommendation[]>>(
      url,
      { credentials: 'include' },
      getLereApiHostOverride(),
    )
    const { lere_version } = response.meta || {}
    const recommendations = response.result.map(recommendation => offerRecommendationMap(recommendation))
    return { recommendations, lereVersion: lere_version }
  } catch (error) {
    logError('getCalendarRecommendations', error, { offerId, region, filters })
    throw error
  }
}

export async function cacheRecommendations(offerId: string, region: string, filters: Pick<Filters, 'duration' | 'rooms'>) {
  try {
    const { duration, rooms } = filters
    if (!getChildrenAgesValid(rooms)) {
      throw new Error('Some children ages are < 0')
    }
    const builder = new RecommendationsURLBuilder(offerId)
      .withLereEnvParameters()
      .withQueryParams({
        duration,
        region,
      })
      .withRoomParams(rooms)
    const url = builder.withCacheBaseUrl().build()
    await request.get<App.ApiResponse<Recommendation[]>>(
      url,
      { credentials: 'include' },
      getLereApiHostOverride(),
    )
  } catch (error) {
    logError('cacheRecommendations', error, { offerId, filters, region })
    throw error
  }
}

export type PolygonMode = 'polygon_restricted' | 'polygon_excluded' | 'all_included'

interface LocationSeededParams {
  destinationId: string,
  polygonMode: PolygonMode,
  nearDestination: boolean,
  checkInDate?: string,
  checkOutDate?: string,
  duration?: number,
  flexibleDaysOnCheckIn?: number,
  useCentroid?: boolean,
}

export function getLocationSeededRecommendations({
  destinationId,
  polygonMode,
  nearDestination,
  checkInDate,
  checkOutDate,
  duration,
  flexibleDaysOnCheckIn,
  useCentroid,
}: LocationSeededParams): Promise<LereResult> {
  const params = {
    ...getLereEnvParameters(),
    destination_id: destinationId,
    polygon_mode: polygonMode,
    near_destination: nearDestination,
    check_in: checkInDate,
    check_out: checkOutDate,
    duration,
    flex: flexibleDaysOnCheckIn,
    use_centroid: useCentroid,
  }

  const baseUrl = '/api/recommendation/locations'
  const url = [baseUrl, qs.stringify(params)].join('?')

  return request.get<App.ApiResponse<Recommendation[]>>(
    url,
    { credentials: 'include' },
    getLereApiHostOverride(),
  ).then((response) => {
    const { lere_version } = response.meta || {}

    const recommendations = response.result.map(recommendation => offerRecommendationMap(recommendation))
    return { recommendations, lereVersion: lere_version }
  })
}

/**
 * Get the list of abandoned carts (each time the user goes to checkout, we save the cart in BE)
 */
export async function getAbandonedCarts(userId: string, region: string) {
  const url = [
    `/api/recommendation/personalisation/${userId}/abandoned_carts`,
    qs.stringify({ region }),
  ].join('?')

  const response = await request.get<App.ApiResponse<LereHighIntent[]>>(
    url,
    { credentials: 'include' },
    getLereApiHostOverride(),
  )

  const recommendations: App.AbandonedCartRecommendation[] = response.result.map(
    (lereOffer) => {
      const { offer_id, offer_type, creation_time, lere_version, ...rest } = lereOffer
      return {
        ...rest,
        offerId: offer_id,
        offerType: offer_type,
        creationTime: creation_time ?? 0,
        lereVersion: lere_version,
      } as App.AbandonedCartRecommendation
    },
  )

  const { lere_version } = response.meta || {}
  return { carts: recommendations, lereVersion: lere_version }
}

export async function saveAbanonedCarts(userId: string, carts: App.AbandonedCartRecommendation[]) {
  const lereCarts: LereHighIntent[] = carts
    .filter(cart => cart.items.length > 0)
    .map(cart => {
      const { offerId, offerType, creationTime, lereVersion, ...rest } = cart
      const lereCart: LereHighIntent = {
        ...rest,
        offer_id: offerId,
        offer_type: offerType,
        creation_time: creationTime,
        lere_version: lereVersion,
      }
      return lereCart
    })

  if (!lereCarts.length) {
    return
  }

  // have to use without_brand=1 to disable the brand preprocessor since we are sending an array
  const url = `/api/recommendation/personalisation/${userId}/abandoned_carts?without_brand=1`

  return await request.post(
    url,
    lereCarts,
    { credentials: 'include' },
    getLereApiHostOverride(),
  ).catch(console.error)
}

async function saveAbandonedCart(userId: string, cart: App.AbandonedCartRecommendation) {
  return await saveAbanonedCarts(userId, [cart])
}

/**
 * We use debounce to prevent frequent API calls when the cart is being updated rapidly during checkout
 */
export const saveCartInLereServiceWithDebounce = debounce(saveAbandonedCart, 500, { trailing: true })

export async function removeAbandonedCarts(userId: string, offerIds: string[]) {
  const url = `/api/recommendation/personalisation/${userId}/abandoned_carts/remove?without_brand=1`

  return await request.post(
    url,
    offerIds,
    { credentials: 'include' },
    getLereApiHostOverride(),
  ).catch(console.error)
}

// result.context.audience_params.offer_id
interface ContextResult {
  context: {
    audience_params?: {
      offer_ids: string[];
    }
  };
}

export async function getPersonalisedContextViewHistory(userId: string) {
  const url = [
    `/api/recommendation/personalisation/${userId}`,
    qs.stringify({ categories: 'context', ...getLereEnvParameters() }),
  ].join('?')

  const response = await request.get<App.ApiResponse<ContextResult>>(
    url,
    { credentials: 'include' },
    getLereApiHostOverride(),
  )

  const offerIds = response.result.context?.audience_params?.offer_ids || []
  const { lere_version } = response.meta || {}
  return { offerIds, lereVersion: lere_version }
}

// no bedbank
const plmSupportedOfferTypes: Array<App.OfferType> = [
  'hotel',
  'last_minute_hotel',
  'tactical_ao_hotel',
  'bundle_and_save',
  'tour',
  'direct_tour',
  'partner_tour',
  'connection_tour',
  'cruise',
]
const plmOfferTypeQuery = plmSupportedOfferTypes.join(',')

export async function getPeopleLikeMeRecommendations(userId: string, region: string) {
  const url = [`/api/recommendation/personalisation/${userId}/people_like_me`,
    qs.stringify({
      region,
      offer_types: plmOfferTypeQuery,
      ...getLereEnvParameters(),
    }),
  ].join('?')
  const response = await request.get<App.ApiResponse<string[] | LereRecommendation[]>>(
    url,
    { credentials: 'include' },
    getLereApiHostOverride(),
  )

  let recommendations: App.RecommendationOffer[] = []
  if (response.result?.length) {
    if (typeof response.result[0] === 'string' || response.result[0] instanceof String) {
      // old api format
      recommendations = (response.result as string[]).map(
        (id) => ({ offerId: id, offerType: detectOfferTypeFromId(id) }),
      )
    } else {
      recommendations = (response.result as LereRecommendation[]).map(
        (lereOffer) => ({ offerId: lereOffer.offer_id, offerType: lereOffer.offer_type }),
      )
    }
  }

  const { lere_version } = response.meta || {}
  return { offers: recommendations, lereVersion: lere_version }
}

export async function getTopDeals(region: string) {
  const response = await request.get<App.ApiResponse<{ offer_id: string, offer_type: string }[]>>(
    ['/api/recommendation/top_deals', qs.stringify({ region, ...getLereEnvParameters() })].join('?'),
    { credentials: 'include' },
    getLereApiHostOverride(),
  )

  const { lere_version } = response.meta || {}
  return { offers: response.result, lereVersion: lere_version }
}

export async function getHeroPlanner(region: string) {
  const response = await request.get<App.ApiResponse<string[]>>(
    ['/api/recommendation/hero_planner', qs.stringify({ region })].join('?'),
    { credentials: 'include' },
    getLereApiHostOverride(),
  )

  const { lere_version } = response.meta || {}
  return { offerIds: response.result, lereVersion: lere_version }
}

interface LereHighIntent {
  offer_id: App.HighIntentOffer['offerId'];
  offerId?: App.HighIntentOffer['offerId'];
  offer_type: App.HighIntentOffer['offerType'];
  creation_time: App.HighIntentOffer['creationTime'];
  lere_version?: App.HighIntentOffer['lereVersion'];
}

// the order matters, front ones returned first, later ones get deduped
const HIGH_INTENT_FETCH_CATEGORIES = 'sticky_abandoned_carts,sticky_hot_leads,recently_viewed'
const HIGH_INTENT_FETCH_LIMIT = 15

export async function getHighIntentOffers(userId: string, region: string) {
  let url = `/api/recommendation/personalisation/${userId}`
  url += `?region=${region}`
  url += `&limit=${HIGH_INTENT_FETCH_LIMIT}`
  url += `&categories=${HIGH_INTENT_FETCH_CATEGORIES}`
  url += '&aggregation_mode=true'
  const response = await request.get<App.ApiResponse<LereHighIntent[]>>(
    url,
    { credentials: 'include' },
    getLereApiHostOverride(),
  )

  const { lere_version: requestLereVersion } = response.meta || {}

  const result = response.result ?? []
  const offers = result.map(lereOffer => {
    const { offer_id, offer_type, creation_time, lere_version, ...rest } = lereOffer
    return {
      ...rest,
      offerId: offer_id ?? lereOffer.offerId, // backward compatibility
      offerType: offer_type,
      creationTime: creation_time ?? 0, // backward compatibility
      lereVersion: lere_version,
    }
  })

  return { offers, lereVersion: requestLereVersion }
}

async function saveHotLeads(userId: string, hotLeadOffers: App.HotLeadOffer[]) {
  if (!hotLeadOffers.length) { return }

  const lereHotLeadOffers = hotLeadOffers.map(hotLead => {
    const { offerId, offerType, creationTime, lereVersion, ...rest } = hotLead
    return {
      ...rest,
      offer_id: offerId,
      offer_type: offerType,
      creation_time: creationTime,
    }
  })

  await request.post(
    `/api/recommendation/personalisation/${userId}/hot_leads?without_brand=1`,
    lereHotLeadOffers,
    { credentials: 'include' },
    getLereApiHostOverride(),
  ).catch(console.error)
}

export const saveHotLeadsWithDebounce = debounce(saveHotLeads, 1000)

export async function removeHotLeads(userId: string, offerIds: string[]) {
  if (!offerIds.length) { return }

  await request.post(
    `/api/recommendation/personalisation/${userId}/hot_leads/remove?without_brand=1`,
    offerIds,
    { credentials: 'include' },
    getLereApiHostOverride(),
  ).catch(console.error)
}

async function saveRecentlyViewedOffers(userId: string, offers: Array<App.RecentlyViewedOffer>) {
  if (!offers.length) { return }

  const lereOffers: LereHighIntent[] = offers.map(offer => {
    const { offerId, offerType, creationTime, lereVersion, ...rest } = offer
    return {
      ...rest,
      offer_id: offerId,
      offer_type: offerType,
      creation_time: creationTime,
    }
  })

  await request.post(
    // we need to post an array and have to disable the brand pre-processor
    // else it will turn the request body into an object
    `/api/recommendation/personalisation/${userId}/recently_viewed?without_brand=1`,
    lereOffers,
    { credentials: 'include' },
    getLereApiHostOverride(),
  ).catch(console.error)
}

export const saveRecentlyViewedOffersWithDebounce = debounce(saveRecentlyViewedOffers, 1000)

export async function removeRecentlyViewedOffers(userId: string, offerIds: string[]) {
  if (!offerIds.length) { return }

  await request.post(
    // we need to post an array and have to disable the brand pre-processor
    // else it will turn the request body into an object
    `/api/recommendation/personalisation/${userId}/recently_viewed/remove?without_brand=1`,
    offerIds,
    { credentials: 'include' },
    getLereApiHostOverride(),
  ).catch(console.error)
}

interface LereRecommendation {
  offer_id: App.RecommendationOffer['offerId'];
  offer_type: App.RecommendationOffer['offerType'];
}

interface AlternativesResult {
  alternatives: LereRecommendation[];
}

const ALTERNATIVE_FETCH_LIMIT = 18
const ALTERNATIVE_MODEL_VERSION = '2_0_0'

export async function getPersonalisedAlternativeRecommendations(userId: string, region: string) {
  let url = `/api/recommendation/personalisation/${userId}`
  url += `?region=${region}`
  url += `&limit=${ALTERNATIVE_FETCH_LIMIT}`
  url += '&categories=alternatives'
  url += `&model_versions=${ALTERNATIVE_MODEL_VERSION}`

  const response = await request.get<App.ApiResponse<AlternativesResult>>(
    url,
    { credentials: 'include' },
    getLereApiHostOverride(),
  )

  const { lere_version: lereVersion } = response.meta || {}

  const alternatives = response.result?.alternatives ?? []
  const offers: App.RecommendationOffer[] = alternatives.map(lereOffer => {
    const { offer_id, offer_type } = lereOffer
    return {
      offerId: offer_id,
      offerType: offer_type,
    }
  })

  return { offers, lereVersion }
}
